/**
 * Shared polling loop for async document-generation jobs.
 *
 * Every document type that follows the async job pattern (create job →
 * poll status → download) must poll through this helper so they all get
 * the same resilience guarantees:
 *
 * - A single failed poll (network blip, request timeout against a
 *   cold-starting Lambda) does NOT fail the job — the backend keeps
 *   processing regardless of whether any individual poll gets through.
 *   Only MAX_CONSECUTIVE_POLL_FAILURES failures in a row give up.
 * - A deliberate abort (user navigating away) stops immediately and is
 *   never retried.
 * - Status requests must use POLL_REQUEST_TIMEOUT_MS so a hung request
 *   drops out of the loop instead of stalling it forever.
 */

// A status poll can land on a cold-starting Lambda, which takes ~8-10s to
// initialise before it can respond. The per-poll request timeout must sit
// comfortably above that, or every poll that lands on a cold container fails.
export const POLL_REQUEST_TIMEOUT_MS = 30000;

// Consecutive (not total) failures before the job is declared unreachable.
// Any successful poll resets the count.
export const MAX_CONSECUTIVE_POLL_FAILURES = 3;

export interface PollJobOptions<T> {
  /**
   * Fetch the job's current status. A rejection is treated as a transient
   * poll failure and retried; it only propagates after
   * MAX_CONSECUTIVE_POLL_FAILURES rejections in a row.
   */
  fetchStatus: () => Promise<T>;
  /** Whether this status ends polling (e.g. completed or failed). */
  isTerminal: (status: T) => boolean;
  /** Called with every successfully fetched status, terminal or not. */
  onStatus?: (status: T) => void;
  /** Message of the error thrown when maxAttempts is exhausted. */
  timeoutMessage: string;
  /** Milliseconds between polls. */
  pollInterval?: number;
  /** Maximum polls (failed polls included) before giving up. */
  maxAttempts?: number;
  /** Callers should abort on unmount so polling stops with the UI. */
  signal?: AbortSignal;
}

/**
 * Poll a job until a terminal status, the abort signal fires, the attempt
 * budget runs out, or the status endpoint is unreachable for
 * MAX_CONSECUTIVE_POLL_FAILURES polls in a row.
 */
export async function pollJob<T>(options: PollJobOptions<T>): Promise<T> {
  const {
    fetchStatus,
    isTerminal,
    onStatus,
    timeoutMessage,
    pollInterval = 2000,
    maxAttempts = 120,
    signal
  } = options;

  let attempts = 0;
  let consecutiveFailures = 0;

  for (;;) {
    if (signal?.aborted) {
      throw new DOMException('Aborted', 'AbortError');
    }
    attempts++;

    try {
      const status = await fetchStatus();
      consecutiveFailures = 0;

      if (onStatus) {
        onStatus(status);
      }
      if (isTerminal(status)) {
        return status;
      }
    } catch (err) {
      // A deliberate abort should stop polling immediately.
      if (signal?.aborted) {
        throw err;
      }
      consecutiveFailures++;
      if (consecutiveFailures >= MAX_CONSECUTIVE_POLL_FAILURES) {
        throw err;
      }
    }

    if (attempts >= maxAttempts) {
      throw new Error(timeoutMessage);
    }

    // Wait out the interval, or reject early if aborted mid-wait.
    await new Promise<void>((resolve, reject) => {
      let onAbort: (() => void) | null = null;
      const timer = setTimeout(() => {
        if (onAbort) signal?.removeEventListener('abort', onAbort);
        resolve();
      }, pollInterval);
      onAbort = () => {
        clearTimeout(timer);
        reject(new DOMException('Aborted', 'AbortError'));
      };
      if (signal) {
        if (signal.aborted) {
          onAbort();
        } else {
          signal.addEventListener('abort', onAbort, { once: true });
        }
      }
    });
  }
}
